// src/pages/invoices/AddInvoice.js
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import AsyncSelect from "react-select/async";
import axios from "axios";
import {
  FiChevronLeft, FiFileText, FiUser, FiCalendar, FiPlus, FiTrash2, FiPercent, FiPackage
} from "react-icons/fi";
import API_BASE_URL from "../../utils/config";

const toNum = (v, d = 0) => (Number.isFinite(Number(v)) ? Number(v) : d);
const r2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
const fmtINR = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(toNum(n));

// --- Batch badge tone helper ---
const EXP_SOON_DAYS = 30;   // <=30 days -> “expiring soon”
const EXP_WARN_DAYS = 90;   // <=90 days -> “stale/approaching”

function batchTone(expiry_date, stock) {
  if (!stock || Number(stock) <= 0) {
    return { text: "Out of stock", cls: "bg-slate-100 text-slate-700 border-slate-200" };
  }
  if (!expiry_date) {
    return { text: "No expiry", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" };
  }
  const d = new Date(expiry_date);
  if (isNaN(d.getTime())) {
    return { text: "Expiry ?", cls: "bg-slate-100 text-slate-700 border-slate-200" };
  }
  const today = new Date();
  d.setHours(0,0,0,0); today.setHours(0,0,0,0);
  const diffDays = Math.round((d - today) / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return { text: "Expired", cls: "bg-rose-50 text-rose-700 border-rose-200" };
  if (diffDays <= EXP_SOON_DAYS) return { text: `Expiring (${diffDays}d)`, cls: "bg-red-50 text-red-700 border-red-200" };
  if (diffDays <= EXP_WARN_DAYS) return { text: `Near (${diffDays}d)`, cls: "bg-amber-50 text-amber-800 border-amber-200" };
  return { text: `Good (${diffDays}d)`, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" };
}

export default function AddInvoice() {
  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem("token");
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const qs = new URLSearchParams(location.search);
  const orderId = qs.get("orderId");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const [customer, setCustomer] = useState({ id: "", name: "", phone: "", address: "" });
  const [invoiceDate, setInvoiceDate] = useState(() => new Date().toISOString().slice(0,10));
  const [dueDate, setDueDate] = useState("");
  const [notes, setNotes] = useState("");

  const [items, setItems] = useState([]);
  const [custPage, setCustPage] = useState(1);
  const [custLimit] = useState(50);
  const [customerQuery, setCustomerQuery] = useState("");

  // ---- Customer search loader ----
  const loadCustomerOptions = async (input) => {
    try {
      if (input !== customerQuery) {
        setCustomerQuery(input);
        setCustPage(1);
      }
      const { data } = await axios.get(
        `${API_BASE_URL}/api/customers`,
        { ...auth, params: { page: custPage, limit: custLimit, q: input || "" } }
      );
      const list = Array.isArray(data) ? data : data?.rows || [];
      return list.map((c) => ({
        value: c.id,
        label: `${c.name}${c.phone ? ` • ${c.phone}` : ""}`,
        raw: c,
      }));
    } catch (e) {
      console.error("customer search error", e);
      return [];
    }
  };

  const loadProductOptions = async (input) => {
    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/api/products/forOrder`,
        { ...auth, params: { q: (input || "").trim() } }
      );
      const list = Array.isArray(data) ? data : data?.rows || [];
      return list.map((p) => ({
        value: p.product_id,
        label: p.product_name,
        raw: p,
      }));
    } catch (e) {
      console.error("product search error", e);
      return [];
    }
  };

  useEffect(() => {
    let abort = false;
    (async () => {
      try {
        setLoading(true);
        setErr("");
        if (orderId) {
          const r = await axios.get(`${API_BASE_URL}/api/invoices/from-order/${orderId}`, auth);
          if (abort) return;
          const d = r.data?.draft || {};
          setCustomer({
            id: d.customer_id || "",
            name: d.customer_name || "",
            phone: d.customer_phone || "",
            address: d.customer_address || "",
          });
          setInvoiceDate(d.invoice_date || new Date().toISOString().slice(0,10));
          setDueDate(d.due_date || "");
          setNotes(d.notes || "");
          setItems(
            Array.isArray(d.items)
              ? d.items.map((it) =>
                  recalcItem({
                    product_id: it.product_id,
                    product_name: it.product_name || "",
                    hsn: it.hsn || "",
                    pack: it.pack || "",
                    mrp: toNum(it.mrp),
                    quantity: toNum(it.quantity, 1),
                    free_qty: toNum(it.free_qty, 0),
                    unit_price: toNum(it.unit_price),
                    discount: toNum(it.discount),
                    gst: toNum(it.gst),
                    batch_no: it.batch_no || "",
                    expiry_date: it.expiry_date || "",
                    current_stock: toNum(it.current_stock, 0),
                    taxable: 0,
                    gst_amount: 0,
                    total_price: 0,
                    _batches: [],
                    _loadingBatches: false,
                  })
                )
              : []
          );
        } else {
          setItems([]);
        }
      } catch (e) {
        console.error(e);
        setErr("Failed to load invoice draft");
      } finally {
        if (!abort) setLoading(false);
      }
    })();
    return () => { abort = true; };
  }, [orderId, auth]);

  // ---- line recompute ----
  function recalcItem(it) {
    const rate = toNum(it.unit_price);
    const disc = Math.min(Math.max(toNum(it.discount), 0), 100);
    const gst  = Math.max(0, toNum(it.gst));
    const qty  = Math.max(1, toNum(it.quantity, 1));
    const discountUnit = r2(rate * (disc / 100));
    const taxableUnit  = r2(rate - discountUnit);
    const gstUnit      = r2(taxableUnit * (gst / 100));
    const taxableLine  = r2(taxableUnit * qty);
    const gstLine      = r2(gstUnit * qty);
    const totalLine    = r2(taxableLine + gstLine);
    return { ...it, taxable: taxableLine, gst_amount: gstLine, total_price: totalLine };
  }

  const updateItem = (idx, key, value) => {
    setItems((prev) => {
      const arr = [...prev];
      arr[idx] = recalcItem({ ...arr[idx], [key]: value });
      return arr;
    });
  };

  // non-recalc patcher (for UI flags/batch lists etc.)
  const patchItem = (idx, patch) => {
    setItems((prev) => {
      const arr = [...prev];
      arr[idx] = { ...arr[idx], ...patch };
      return arr;
    });
  };

  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

  // batches loader for a row (also toggles open/close)
  const loadBatches = async (idx) => {
    const row = items[idx];
    if (!row?.product_id) return;

    // close if already open
    if (row?._batches?.length) {
      patchItem(idx, { _batches: [] });
      return;
    }

    try {
      patchItem(idx, { _loadingBatches: true });
      const r = await axios.get(`${API_BASE_URL}/api/products/${row.product_id}/batches`, auth);
      const list = Array.isArray(r.data) ? r.data : r.data?.rows || [];
      patchItem(idx, { _batches: list, _loadingBatches: false });
    } catch (e) {
      console.error("batches fetch error", e);
      patchItem(idx, { _batches: [], _loadingBatches: false });
    }
  };

  // totals
  const totals = useMemo(() => {
    let subtotal = 0, gst_total = 0, discount_total = 0, grand_total = 0;
    items.forEach((it) => {
      const rate = toNum(it.unit_price);
      const disc = Math.min(Math.max(toNum(it.discount), 0), 100);
      const gst  = Math.max(0, toNum(it.gst));
      const qty  = Math.max(1, toNum(it.quantity, 1));
      const discountUnit = r2(rate * (disc / 100));
      const taxableUnit  = r2(rate - discountUnit);
      const gstUnit      = r2(taxableUnit * (gst / 100));
      subtotal       += r2(taxableUnit * qty);
      gst_total      += r2(gstUnit * qty);
      discount_total += r2(discountUnit * qty);
      grand_total    += r2((taxableUnit + gstUnit) * qty);
    });
    return { subtotal, gst_total, discount_total, grand_total };
  }, [items]);

  const saveInvoice = async () => {
    if (!customer.id) return alert("Customer is missing.");
    if (items.length === 0) return alert("At least one line item is required.");

    try {
      setSaving(true);
      const payload = {
        customer_id: Number(customer.id),
        order_id: orderId ? Number(orderId) : null,
        invoice_date: invoiceDate,
        due_date: dueDate || null,
        notes,
        items: items.map((i) => ({
          product_id: Number(i.product_id),
          batch_no: i.batch_no || null,
          quantity: Number(i.quantity),
          free_qty: Number(i.free_qty || 0),
          unit_price: Number(i.unit_price),
          discount: Number(i.discount),
          gst: Number(i.gst),
          hsn: i.hsn || null,
          pack: i.pack || null,
          mrp: Number(i.mrp || 0),
        })),
      };
      const r = await axios.post(`${API_BASE_URL}/api/invoices/save`, payload, auth);
      const id = r.data?.id;
      alert("Invoice saved");
      navigate(id ? `/invoices/${id}` : "/invoices");
    } catch (e) {
      console.error(e);
      alert("Failed to save invoice");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 pb-28">
      {/* Top bar */}
      <div className="sticky top-0 z-10 -mx-4 px-4 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/invoices" className="inline-flex items-center gap-1 text-white/90 hover:text-white">
              <FiChevronLeft /> Back
            </Link>
            <div className="flex items-center gap-2 font-semibold text-lg">
              <FiFileText className="opacity-90" />
              {orderId ? `Create Invoice (from Order #${orderId})` : "Create Invoice"}
            </div>
          </div>
          <div className="text-white/90 text-sm">
            {items.length} item{items.length !== 1 ? "s" : ""} • {fmtINR(totals.grand_total)}
          </div>
        </div>
      </div>

      {err && <div className="mt-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{err}</div>}
      {loading && <div className="mt-4 rounded-md border bg-white px-3 py-2 text-sm text-slate-600">Loading draft…</div>}

      {!loading && (
        <>
          {/* Header */}
          <div className="mt-6 bg-white rounded-2xl shadow-sm border p-4 md:p-6">
            <div className="flex items-center gap-2 mb-4">
              <FiUser className="text-blue-600" />
              <h3 className="font-semibold text-lg">Customer & Dates</h3>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-600 mb-1 block">Customer</label>
                <AsyncSelect
                  cacheOptions
                  defaultOptions
                  loadOptions={loadCustomerOptions}
                  getOptionValue={(o) => String(o.value)}
                  getOptionLabel={(o) => o.label}
                  onInputChange={(val) => { setCustomerQuery(val); setCustPage(1); return val; }}
                  value={
                    customer.id
                      ? { value: customer.id, label: `${customer.name}${customer.phone ? ` • ${customer.phone}` : ""}` }
                      : null
                  }
                  onChange={(opt) => {
                    const c = opt?.raw;
                    setCustomer({
                      id: c?.id || "",
                      name: c?.name || "",
                      phone: c?.phone || "",
                      address: c?.address || "",
                    });
                  }}
                  placeholder="Search name or phone…"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={{
                    control: (b) => ({ ...b, borderRadius: 12, minHeight: 44 }),
                    menu: (b) => ({ ...b, zIndex: 30 }),
                    menuPortal: (b) => ({ ...b, zIndex: 9999 }),
                  }}
                />

                {(customer.phone || customer.address) && (
                  <div className="mt-1 text-xs text-gray-600">
                    {customer.phone ? `📞 ${customer.phone}` : ""} {customer.address ? `• ${customer.address}` : ""}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-gray-600 mb-1 block">Invoice Date</label>
                  <input type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} className="w-full border rounded-xl px-3 py-2.5" />
                </div>
                <div>
                  <label className="text-sm text-gray-600 mb-1 block">Due Date</label>
                  <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="w-full border rounded-xl px-3 py-2.5" />
                </div>
              </div>
            </div>

            <div className="mt-4">
              <label className="text-sm text-gray-600 mb-1 block">Notes</label>
              <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full border rounded-xl px-3 py-2" placeholder="Add remarks visible on invoice…" />
            </div>
          </div>

          {/* Items */}
          <div className="mt-6 bg-white rounded-2xl shadow-sm border overflow-hidden">
            <div className="flex items-center justify-between px-4 md:px-6 py-4 border-b">
              <div className="flex items-center gap-2">
                <FiPackage className="text-blue-600" />
                <h3 className="font-semibold text-lg">Items</h3>
              </div>
              <button
                onClick={() =>
                  setItems((prev) => [
                    ...prev,
                    recalcItem({
                      product_id: "", product_name: "",
                      hsn: "", pack: "", mrp: 0,
                      quantity: 1, free_qty: 0,
                      unit_price: 0, discount: 0, gst: 0,
                      batch_no: "", expiry_date: "", current_stock: 0,
                      taxable: 0, gst_amount: 0, total_price: 0,
                      _batches: [], _loadingBatches: false,
                    }),
                  ])
                }
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-green-600 text-white hover:bg-green-700"
              >
                <FiPlus /> Add Row
              </button>
            </div>

            {/* header (desktop) */}
            {items.length > 0 && (
              <div className="hidden md:grid grid-cols-12 gap-2 px-6 py-2 text-[11px] font-semibold uppercase text-gray-500 bg-gray-50">
                <div className="col-span-3">Description</div>
                <div className="col-span-1">Pack</div>
                <div className="col-span-1">HSN</div>
                <div className="col-span-2">Batch / Exp / Stock</div>
                <div className="col-span-1">MRP</div>
                <div className="col-span-1">Rate</div>
                <div className="col-span-1">Qty</div>
                <div className="col-span-1">Free</div>
                <div className="col-span-1">Disc %</div>
                <div className="col-span-1">GST %</div>
              </div>
            )}

            <div className="divide-y">
              {items.map((it, idx) => {
                const over = (toNum(it.quantity, 1) + toNum(it.free_qty, 0)) > toNum(it.current_stock, 0);
                return (
                  <div key={idx} className="px-4 md:px-6 py-3">
                    {/* DESKTOP */}
                    <div className="hidden md:grid grid-cols-12 gap-2 items-center">
                      {/* Product picker */}
                      <AsyncSelect
                        className="col-span-3"
                        cacheOptions
                        defaultOptions={false}
                        loadOptions={loadProductOptions}
                        getOptionValue={(o) => String(o.value)}
                        getOptionLabel={(o) => o.label}
                        value={it.product_id ? { value: it.product_id, label: it.product_name } : null}
                        onChange={(opt) => {
                          const p = opt?.raw;
                          if (!p) return;
                          updateItem(idx, "product_id", p.product_id);
                          updateItem(idx, "product_name", p.product_name || "");
                          updateItem(idx, "pack", p.pack || "");
                          updateItem(idx, "hsn",  p.hsn  || "");
                          updateItem(idx, "gst", Number(p.gst_percent || 0));
                          updateItem(idx, "mrp", Number(p.latest_mrp || 0));
                          updateItem(idx, "unit_price", Number(p.rate_excl_gst_margin20 || 0));
                          patchItem(idx, { _batches: [], batch_no: "", expiry_date: "", current_stock: 0 });
                        }}
                        placeholder="Type to search product…"
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={{
                          control: (b) => ({ ...b, minHeight: 40 }),
                          menu: (b) => ({ ...b, zIndex: 30 }),
                          menuPortal: (b) => ({ ...b, zIndex: 9999 }),
                        }}
                      />

<input className="col-span-1 border rounded-lg px-2 py-2 bg-gray-50 cursor-not-allowed" value={it.pack} readOnly placeholder="Pack" />
<input className="col-span-1 border rounded-lg px-2 py-2 bg-gray-50 cursor-not-allowed" value={it.hsn} readOnly placeholder="HSN" />
                      <div className="col-span-2">
                        <div className="flex gap-2 items-center">
                          <button type="button" onClick={() => loadBatches(idx)} className="flex-1 border rounded-lg px-2 py-2 text-left">
                            {it.batch_no ? `Batch: ${it.batch_no}` : "Select batch"}
                          </button>
                          {it.current_stock != null && (
                            <span className={`px-2 py-2 rounded text-xs ${over ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-700"}`}>
                              {it.expiry_date ? new Date(it.expiry_date).toLocaleDateString() : "exp?"} • stk {it.current_stock ?? 0}
                            </span>
                          )}
                        </div>

                        {/* Legend */}
                        

                        {!it._loadingBatches && it._batches.length > 0 && (
                          <div className="mt-2 border rounded-lg p-2 max-h-40 overflow-auto">
                            {it._batches.map((b) => {
                              const tone = batchTone(b.expiry_date, b.current_stock);
                              return (
                                <button
                                  key={`${b.batch_no}`}
                                  type="button"
                                  onClick={() => {
                                    patchItem(idx, {
                                      batch_no: b.batch_no,
                                      expiry_date: b.expiry_date || "",
                                      current_stock: b.current_stock || 0,
                                      _batches: [], // close list
                                    });
                                    if (b.mrp != null) updateItem(idx, "mrp", b.mrp);
                                  }}
                                  className="block w-full text-left px-2 py-2 hover:bg-gray-50 rounded border mb-1"
                                >
                                  <div className="flex items-center justify-between gap-2">
                                    <div className="font-medium">{b.batch_no}</div>
                                    <span className={`text-xs px-2 py-1 rounded-full border ${tone.cls}`}>
                                      {tone.text}
                                    </span>
                                  </div>
                                  <div className="text-xs text-slate-600 mt-1">
                                    {b.expiry_date ? `Exp ${new Date(b.expiry_date).toLocaleDateString()}` : "No expiry"}
                                    {" • "}Stock {b.current_stock ?? 0}
                                    {b.mrp ? ` • MRP ${b.mrp}` : ""}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        )}
                        {it._loadingBatches && <div className="text-xs text-slate-500 mt-1">Loading batches…</div>}
                      </div>
                    <input type="number" inputMode="decimal" className="col-span-1 border rounded-lg px-2 py-2 bg-gray-50 cursor-not-allowed" value={it.mrp} readOnly />
                      <input type="number" inputMode="decimal" className="col-span-1 border rounded-lg px-2 py-2" value={it.unit_price} onChange={(e)=>updateItem(idx,"unit_price",e.target.value)} />
                      <input type="number" inputMode="numeric" min={1} className="col-span-1 border rounded-lg px-2 py-2" value={it.quantity} onChange={(e)=>updateItem(idx,"quantity",e.target.value)} />
                      <input type="number" inputMode="numeric" min={0} className="col-span-1 border rounded-lg px-2 py-2" value={it.free_qty} onChange={(e)=>updateItem(idx,"free_qty",e.target.value)} />
                      <div className="col-span-1 flex items-center gap-1">
                        <input type="number" inputMode="decimal" min={0} max={100} className="w-full border rounded-lg px-2 py-2" value={it.discount} onChange={(e)=>updateItem(idx,"discount",e.target.value)} />
                        <FiPercent className="text-gray-400" />
                      </div>
                      <div className="col-span-1 flex items-center gap-1">
                        <input type="number" inputMode="decimal" min={0} className="w-full border rounded-lg px-2 py-2" value={it.gst} onChange={(e)=>updateItem(idx,"gst",e.target.value)} />
                        <FiPercent className="text-gray-400" />
                      </div>

                      {/* Per-line totals (desktop footer) */}
                      <div className="col-span-12 grid grid-cols-3 gap-2 text-right text-sm mt-1">
                        <div>Taxable: <span className="font-medium">{fmtINR(it.taxable)}</span></div>
                        <div>GST: <span className="font-medium">{fmtINR(it.gst_amount)}</span></div>
                        <div>Total: <span className="font-semibold">{fmtINR(it.total_price)}</span></div>
                      </div>
                    </div>

                    {/* MOBILE */}
                    <div className="md:hidden space-y-3">
                      {/* Product picker */}
                      <AsyncSelect
                        cacheOptions
                        defaultOptions={false}
                        loadOptions={loadProductOptions}
                        getOptionValue={(o) => String(o.value)}
                        getOptionLabel={(o) => o.label}
                        value={it.product_id ? { value: it.product_id, label: it.product_name } : null}
                        onChange={(opt) => {
                          const p = opt?.raw;
                          if (!p) return;
                          updateItem(idx, "product_id", p.product_id);
                          updateItem(idx, "product_name", p.product_name || "");
                          updateItem(idx, "pack", p.pack || "");
                          updateItem(idx, "hsn",  p.hsn  || "");
                          updateItem(idx, "gst", Number(p.gst_percent || 0));
                          updateItem(idx, "mrp", Number(p.latest_mrp || 0));
                          updateItem(idx, "unit_price", Number(p.rate_excl_gst_margin20 || 0));
                          patchItem(idx, { _batches: [], batch_no: "", expiry_date: "", current_stock: 0 });
                        }}
                        placeholder="Search product…"
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={{
                          control: (b) => ({ ...b, minHeight: 44, borderRadius: 10 }),
                          menu: (b) => ({ ...b, zIndex: 30 }),
                          menuPortal: (b) => ({ ...b, zIndex: 9999 }),
                        }}
                      />

                      {/* Pack + HSN */}
                      <div className="grid grid-cols-2 gap-2">
                        <input className="col-span-1 border rounded-lg px-3 py-2 bg-gray-50 cursor-not-allowed" value={it.pack} readOnly placeholder="Pack" />
                        <input className="col-span-1 border rounded-lg px-2 py-2 bg-gray-50 cursor-not-allowed" value={it.hsn} readOnly placeholder="HSN" />                      
                </div>

                      {/* Batch selector with colored chips */}
                      <div>
                        <button type="button" onClick={() => loadBatches(idx)} className="w-full border rounded-lg px-3 py-2 text-left">
                          {it.batch_no
                            ? `Batch: ${it.batch_no} • ${it.expiry_date ? new Date(it.expiry_date).toLocaleDateString() : "exp?"} • stk ${it.current_stock ?? 0}`
                            : "Select batch"}
                        </button>


                        {it._loadingBatches && (<div className="text-xs text-slate-500 mt-1">Loading batches…</div>)}

                        {!it._loadingBatches && it._batches.length > 0 && (
                          <div className="mt-2 border rounded-lg p-2 max-h-60 overflow-auto">
                            {it._batches.map((b) => {
                              const tone = batchTone(b.expiry_date, b.current_stock);
                              return (
                                <button
                                  key={`${b.batch_no}`}
                                  type="button"
                                  onClick={() => {
                                    patchItem(idx, {
                                      batch_no: b.batch_no,
                                      expiry_date: b.expiry_date || "",
                                      current_stock: b.current_stock || 0,
                                      _batches: [], // close list
                                    });
                                    if (b.mrp != null) updateItem(idx, "mrp", b.mrp);
                                  }}
                                  className="block w-full text-left px-2 py-2 hover:bg-gray-50 rounded border mb-1"
                                >
                                  <div className="flex items-center justify-between gap-2">
                                    <div className="font-medium truncate">{b.batch_no}</div>
                                    <span className={`text-[11px] px-2 py-0.5 rounded-full border ${tone.cls}`}>{tone.text}</span>
                                  </div>
                                  <div className="text-[12px] text-slate-600 mt-1">
                                    {b.expiry_date ? `Exp ${new Date(b.expiry_date).toLocaleDateString()}` : "No expiry"}
                                    {" • "}Stock {b.current_stock ?? 0}
                                    {b.mrp ? ` • MRP ${b.mrp}` : ""}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {/* Qty / Free */}
                      <div className="grid grid-cols-2 gap-2">
                        <input type="number" inputMode="numeric" min={1} className="border rounded-lg px-3 py-2" value={it.quantity} onChange={(e) => updateItem(idx, "quantity", e.target.value)} placeholder="Qty" />
                        <input type="number" inputMode="numeric" min={0} className="border rounded-lg px-3 py-2" value={it.free_qty} onChange={(e) => updateItem(idx, "free_qty", e.target.value)} placeholder="Free" />
                      </div>

                      {/* Rate / Disc % / GST % */}
                      <div className="grid grid-cols-3 gap-2">
                        <input type="number" inputMode="decimal" className="border rounded-lg px-3 py-2" value={it.unit_price} onChange={(e) => updateItem(idx, "unit_price", e.target.value)} placeholder="Rate" />
                        <div className="flex items-center gap-1">
                          <input type="number" inputMode="decimal" min={0} max={100} className="w-full border rounded-lg px-3 py-2" value={it.discount} onChange={(e) => updateItem(idx, "discount", e.target.value)} placeholder="Disc %" />
                          <FiPercent className="text-gray-400" />
                        </div>
                        <div className="flex items-center gap-1">
                          <input type="number" inputMode="decimal" min={0} className="w-full border rounded-lg px-3 py-2" value={it.gst} onChange={(e) => updateItem(idx, "gst", e.target.value)} placeholder="GST %" />
                          <FiPercent className="text-gray-400" />
                        </div>
                      </div>

                      {/* MRP + line total + warnings */}
                      <div className="grid grid-cols-2 gap-2 items-center">
                       <input type="number" inputMode="decimal" className="border rounded-lg px-3 py-2 bg-gray-50 cursor-not-allowed" value={it.mrp} readOnly placeholder="MRP" />
                        <div className="text-right font-semibold">{fmtINR(it.total_price)}</div>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className={`text-xs ${ (toNum(it.quantity,1) + toNum(it.free_qty,0)) > toNum(it.current_stock,0) ? "text-amber-700" : "text-slate-500"}`}>
                          {(toNum(it.quantity,1) + toNum(it.free_qty,0)) > toNum(it.current_stock,0) ? "Warning: qty+free exceeds stock" : ""}
                        </div>
                        <button onClick={() => removeItem(idx)} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-red-600 border-red-200 hover:bg-red-50">
                          <FiTrash2 /> Remove
                        </button>
                      </div>
                    </div>

                    {/* row actions (desktop) */}
                    <div className="hidden md:flex justify-end mt-2">
                      <button
                        onClick={() => removeItem(idx)}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-red-600 border-red-200 hover:bg-red-50"
                      >
                        <FiTrash2 /> Remove
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {!items.length && (
              <div className="px-6 py-10 text-center text-gray-500">
                No items yet. Click <span className="font-medium text-green-700">Add Row</span> to begin.
              </div>
            )}
          </div>

          {/* Totals */}
          <div className="mt-6 bg-white rounded-2xl shadow-sm border p-4 md:p-6">
            <div className="flex items-center gap-2 mb-4">
              <FiCalendar className="text-blue-600" />
              <h3 className="font-semibold text-lg">Totals</h3>
            </div>
            <div className="grid md:grid-cols-4 gap-4 text-sm">
              <div className="rounded-xl border p-3">
                <div className="text-gray-500">Subtotal (Taxable)</div>
                <div className="font-semibold text-gray-900">{fmtINR(totals.subtotal)}</div>
              </div>
              <div className="rounded-xl border p-3">
                <div className="text-gray-500">Discount</div>
                <div className="font-semibold text-gray-900">{fmtINR(totals.discount_total)}</div>
              </div>
              <div className="rounded-xl border p-3">
                <div className="text-gray-500">GST</div>
                <div className="font-semibold text-gray-900">{fmtINR(totals.gst_total)}</div>
              </div>
              <div className="rounded-xl border p-3 bg-gray-50">
                <div className="text-gray-700">Grand Total</div>
                <div className="text-xl font-bold">{fmtINR(totals.grand_total)}</div>
              </div>
            </div>
          </div>

          {/* Sticky Save */}
          <div className="fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur border-t shadow-sm">
            <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                {items.length} item{items.length !== 1 ? "s" : ""} • <span className="font-semibold">{fmtINR(totals.grand_total)}</span>
              </div>
              <button
                onClick={saveInvoice}
                disabled={saving || !customer.id || items.length === 0}
                className="px-5 py-2.5 rounded-xl bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {saving ? "Saving…" : "Save Invoice"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
